import { type FC, useState, useEffect, useRef } from 'react';
import { type GraphQLTaggedNode, graphql, useFragment } from 'react-relay';

import { useSubscriptionRefetch } from 'dibs-subscriptions-utils/exports/useSubscriptionRefetch.ts';
import { useAuctionState } from 'dibs-auctions/exports/hooks/useAuctionState.ts';

import { AuctionItemSubscriptionErrorLazy } from './AuctionItemSubscriptionErrorLazy.tsx';

import { type AuctionItemSubscription_item$key } from './__generated__/AuctionItemSubscription_item.graphql.ts';

type Props = {
    item: AuctionItemSubscription_item$key | null | undefined;
    subscriptionConfig: {
        subscription: GraphQLTaggedNode;
        variables: { input: { itemId: string } & Record<string, unknown> };
    };
    refetch: () => void;
    afterDisplay?: boolean;
};

const MAX_REFETCHES = 5;
const CONNECTION_DOWN_MAX = 1000 * 60 * 1.5;

const shouldAlertImmediately = (endDate?: string | null): boolean => {
    if (endDate) {
        return new Date(endDate).getTime() - new Date().getTime() <= CONNECTION_DOWN_MAX;
    }
    return false;
};

export const AuctionItemSubscription: FC<Props> = ({
    item: itemRef,
    subscriptionConfig,
    refetch,
    afterDisplay = false,
}) => {
    const [hasSubscriptionError, setHasSubscriptionError] = useState(false);
    const item = useFragment(
        graphql`
            fragment AuctionItemSubscription_item on Item
            @argumentDefinitions(page: { type: "PageDisplayEnum" }) {
                isSold
                isUnavailable
                relevantAuctionLot(page: $page) {
                    endDate
                }

                ...useAuctionState_item @arguments(page: $page)
            }
        `,
        itemRef
    );

    const { relevantAuctionLot, isSold, isUnavailable } = item || {};

    const endDate = relevantAuctionLot?.endDate;
    const endDateRef = useRef<string | null | undefined>(endDate);

    const {
        isPreviewAuction,
        isUnavailableAuction,
        hasAuctionEndedWithBIN,
        hasAuctionEndedWithWinner,
    } = useAuctionState({ item });

    const itemIsUnavailable =
        isUnavailableAuction ||
        (hasAuctionEndedWithBIN && !isSold) || // item was sold with BIN and then canceled
        isUnavailable;

    const setupSubscription =
        !hasAuctionEndedWithWinner && !itemIsUnavailable && !isPreviewAuction && !isSold;

    const { isConnected, reconnectRefetches, hadError } = useSubscriptionRefetch(
        { ...subscriptionConfig, onError: () => setHasSubscriptionError(true) },
        {
            skip: !setupSubscription,
            refetch,
            refetchOnReconnect: true,
            pollWhileDisconnected: true,
            maxReconnectRefetches: MAX_REFETCHES,
            maxPollingRefetches: MAX_REFETCHES,
        }
    );

    useEffect(() => {
        endDateRef.current = endDate;
    }, [endDate]);

    useEffect(() => {
        let errorTimer: ReturnType<typeof setTimeout> | null = null;
        if (setupSubscription && !isConnected && !errorTimer && hadError) {
            errorTimer = setTimeout(
                () => {
                    setHasSubscriptionError(true);
                },
                shouldAlertImmediately(endDateRef.current) || reconnectRefetches >= MAX_REFETCHES
                    ? 0
                    : CONNECTION_DOWN_MAX
            );
        }

        return () => {
            if (errorTimer) {
                clearTimeout(errorTimer);
            }
        };
    }, [isConnected, setupSubscription, reconnectRefetches, hadError]);
    return (
        <AuctionItemSubscriptionErrorLazy
            hasSubscriptionError={hasSubscriptionError}
            afterDisplay={afterDisplay}
        />
    );
};
